import { v4 } from 'uuid';
import React from 'react';
import {
    BsBriefcase,
    BsBrush,
    BsBuilding,
    BsCardHeading,
    BsFileEarmarkPerson,
    BsGenderFemale,
    BsPeople,
    BsSunglasses,
    BsBracesAsterisk,
    BsGenderMale,
    BsExplicit,
} from 'react-icons/bs';
import { TbTargetOff, TbEqualNot } from 'react-icons/tb';
import { BiBrain, BiSleepy } from 'react-icons/bi';
import {
    MdAttachMoney,
    MdMoneyOff,
    MdOutlineLabel,
    MdOutlineSentimentDissatisfied,
    MdScience,
    MdSentimentSatisfiedAlt,
} from 'react-icons/md';
import { CgGym } from 'react-icons/cg';

interface WordListWithoutId {
    name: string;
    symbol: JSX.Element;
    words: string[];
}

export interface WordList extends WordListWithoutId {
    id: string;
}

export const Appearance: WordListWithoutId = {
    name: 'Appearance',
    symbol: <BsSunglasses size={14} />,
    words: [
        'alluring',
        'voluptuous',
        'blushing',
        'homely',
        'plump',
        'sensual',
        'gorgeous',
        'slim',
        'bald',
        'athletic',
        'fashionable',
        'stout',
        'ugly',
        'muscular',
        'slender',
        'feeble',
        'handsome',
        'healthy',
        'attractive',
        'fat',
        'weak',
        'thin',
        'pretty',
        'beautiful',
        'strong',
    ],
};

export const Arts: WordListWithoutId = {
    name: 'Arts',
    symbol: <BsBrush size={14} />,
    words: ['poetry', 'art', 'Shakespeare', 'dance', 'literature', 'novel', 'symphony', 'drama'],
};

export const Career: WordListWithoutId = {
    name: 'Career',
    symbol: <BsBuilding size={14} />,
    words: ['executive', 'management', 'professional', 'corporation', 'salary', 'office', 'business', 'career'],
};

export const Characteristics: WordListWithoutId = {
    name: 'Characteristics',
    symbol: <BsFileEarmarkPerson size={14} />,
    words: [
        'Active',
        'Admirable',
        'Adventurous',
        'Agreeable',
        'Amiable',
        'Amusing',
        'Appreciative',
        'Athletic',
        'Authentic',
        'Benevolent',
        'Brave',
        'Bright',
        'Brilliant',
        'Calm',
        'Capable',
        'Caring',
        'Charming',
        'Cheerful',
        'Clean',
        'Clear-headed',
        'Clever',
        'Compassionate',
        'Confident',
        'Considerate',
        'Cooperative',
        'Courageous',
        'Courteous',
        'Creative',
        'Curious',
        'Dedicated',
        'Easygoing',
        'Educated',
        'Enthusiastic',
        'Ethical',
        'Exciting',
        'Extraordinary',
        'Fair',
        'Firm',
        'Focused',
        'Forgiving',
        'Friendly',
        'Generous',
        'Gentle',
        'Good-natured',
        'Grateful',
        'Happy',
        'Hardworking',
        'Helpful',
        'Heroic',
        'Honest',
        'Hopeful',
        'Humble',
        'Innocent',
        'Intelligent',
        'Inventive',
        'Joyful',
        'Kind',
        'Lively',
        'Loving',
        'Loyal',
        'Neat',
        'Nice',
        'Optimistic',
        'Organized',
        'Passionate',
        'Patient',
        'Peaceful',
        'Playful',
        'Polite',
        'Principled',
        'Reliable',
        'Respectful',
        'Responsible',
        'Self-disciplined',
        'Selfless',
        'Sincere',
        'Skillful',
        'Strong',
        'Sweet',
        'Thoughtful',
        'Trustworthy',
        'Understanding',
        'Unselfish',
        'Wise',
        'Aggressive',
        'Angry',
        'Anxious',
        'Argumentative',
        'Arrogant',
        'Bored',
        'Bossy',
        'Brutal',
        'Careless',
        'Charmless',
        'Clumsy',
        'Conceited',
        'Cowardly',
        'Critical',
        'Cruel',
        'Dangerous',
        'Deceitful',
        'Destructive',
        'Devious',
        'Difficult',
        'Discouraging',
        'Discourteous',
        'Dishonest',
        'Disloyal',
        'Disobedient',
        'Disorganized',
        'Disrespectful',
        'Disruptive',
        'Envious',
        'Fearful',
        'Foolish',
        'Forgetful',
        'Frightening',
        'Gloomy',
        'Greedy',
        'Grim',
        'Hateful',
        'Haughty',
        'Hostile',
        'Ignorant',
        'Impatient',
        'Impractical',
        'Inconsiderate',
        'Insincere',
        'Insulting',
        'Intolerant',
        'Irresponsible',
        'Irritable',
        'Jealous',
        'Lazy',
        'Liar',
        'Mean',
        'Meddlesome',
        'Messy',
        'Miserable',
        'Monstrous',
        'Moody',
        'Negative',
        'Neglectful',
        'Obnoxious',
        'Petty',
        'Possessive',
        'Power-hungry',
        'Prejudiced',
        'Resentful',
        'Rude',
        'Scornful',
        'Selfish',
        'Shallow',
        'Sloppy',
        'Sneaky',
        'Snobbish',
        'Thoughtless',
        'Unappreciative',
        'Uncaring',
        'Uncooperative',
        'Unforgiving',
        'Unfriendly',
        'Ungrateful',
        'Unhealthy',
        'Unreliable',
        'Violent',
        'Weak',
        'Wicked',
    ],
};

export const Family: WordListWithoutId = {
    name: 'Family',
    symbol: <BsPeople size={14} />,
    words: ['home', 'parents', 'children', 'family', 'cousins', 'marriage', 'wedding', 'relatives'],
};

export const FemaleNouns: WordListWithoutId = {
    name: 'Female Nouns',
    symbol: (
        <svg width={22} height={14}>
            <MdOutlineLabel x={0} y={0} size={14} />
            <BsGenderFemale x={12} y={0} size={10} />
        </svg>
    ),
    words: [
        'actress',
        'actresses',
        'airwoman',
        'airwomen',
        'aunt',
        'aunts',
        'girl',
        'girls',
        'sister',
        'sisters',
        'businesswoman',
        'businesswomen',
        'chairwoman',
        'chairwomen',
        'chick',
        'chicks',
        'mom',
        'moms',
        'mommy',
        'mommies',
        "ma'am",
        'mother',
        'mothers',
        'daughter',
        'daughters',
        'female',
        'females',
        'lady',
        'ladies',
        'granddaughter',
        'granddaughters',
        'she',
        'herself',
        'her',
        'hers',
        'wife',
        'wives',
        'queen',
        'queens',
        'woman',
        'women',
        'mrs.',
        'miss',
        'ms.',
        'policewoman',
        'princess',
        'princesses',
        'spokeswoman',
        'spokeswomen',
        'cowgirl',
        'cowgirls',
        'camerawoman',
        'busgirl',
        'busgirls',
        'bellgirl',
        'bellgirls',
        'barwoman',
        'barwomen',
        'priestess',
        'priestesses',
        'seamstress',
        "seamstress'",
        'governess',
        'governesses',
        'adultress',
        'adultresses',
        'godess',
        'godesses',
        'hostess',
        'hostesses',
        'abbess',
        'abbesses',
        'spinster',
        'spinsters',
        'baroness',
        'barnoesses',
        'belle',
        'belles',
        'bride',
        'brides',
        'duchess',
        'duchesses',
        'empress',
        'empresses',
        'enchantress',
        'fiancee',
        'fiancees',
        'grandmother',
        'grandmothers',
        'headmistress',
        'headmistresses',
        'heroine',
        'heroines',
        'lass',
        'lasses',
        'landlady',
        'landladies',
        'maidservant',
        'maidservants',
        'marchioness',
        'masseuse',
        'masseuses',
        'mistress',
        'mistresses',
        'nun',
        'nuns',
        'niece',
        'nieces',
        'sorceress',
        'sorceresses',
        'stepmother',
        'stepmothers',
        'stepdaughter',
        'stepdaughters',
        'stewardess',
        'stewardesses',
        'waitress',
        'waitresses',
        'widow',
        'widows',
        'witch',
        'witches',
    ],
};

export const FemaleOccupations: WordListWithoutId = {
    name: 'Female Occupations',
    symbol: (
        <svg width={22} height={14}>
            <BsBriefcase x={0} y={0} size={14} />
            <BsGenderFemale x={12} y={0} size={10} />
        </svg>
    ),
    words: [
        'kindergarten teacher',
        'dental hygienist',
        'speech-language pathologist',
        'dental assistant',
        'childcare worker',
        'medical records technician',
        'secretary',
        'medical assistant',
        'hairdresser',
        'dietitian',
        'vocational nurse',
        'teacher assistant',
        'paralegal',
        'billing clerk',
        'phlebotomist',
        'receptionist',
        'housekeeper',
        'registered nurse',
        'bookkeeper',
        'health aide',
        'dressmaker',
        'nurse',
        'maid',
        'librarian',
        'cook',
        'waitress',
        'teacher',
        'cleaner',
        'photographer',
        'writer',
        'model',
        'author',
        'actress',
    ],
};

export const FemalePersonNames: WordListWithoutId = {
    name: 'Female Person Names',
    symbol: (
        <svg width={22} height={14}>
            <BsCardHeading x={0} y={0} size={14} />
            <BsGenderFemale x={12} y={0} size={10} />
        </svg>
    ),
    words: [
        'olivia',
        'emma',
        'ava',
        'sophia',
        'isabella',
        'charlotte',
        'amelia',
        'mia',
        'harper',
        'abigail',
        'emily',
        'ella',
        'elizabeth',
        'camila',
        'luna',
        'sofia',
        'avery',
        'mila',
        'aria',
        'scarlett',
        'penelope',
        'layla',
        'chloe',
        'victoria',
        'madison',
        'eleanor',
        'grace',
        'nora',
        'riley',
        'zoey',
        'hannah',
        'hazel',
        'lily',
        'ellie',
        'lillian',
        'zoe',
        'stella',
        'aurora',
        'natalie',
        'emilia',
        'everly',
        'leah',
        'aubrey',
        'willow',
        'addison',
        'lucy',
        'audrey',
        'bella',
        'nova',
        'paisley',
        'claire',
        'skylar',
        'isla',
        'genesis',
        'naomi',
        'elena',
        'caroline',
        'eliana',
        'anna',
        'valentina',
        'kennedy',
        'ivy',
        'aaliyah',
        'cora',
        'kinsley',
        'hailey',
        'gabriella',
        'allison',
        'gianna',
        'serenity',
        'samantha',
        'sarah',
        'quinn',
        'eva',
        'piper',
        'sophie',
        'sadie',
        'josephine',
        'nevaeh',
        'adeline',
        'arya',
        'emery',
        'lydia',
        'clara',
        'vivian',
        'madeline',
        'peyton',
        'julia',
        'rylee',
        'serena',
        'mandy',
        'alice',
        'jessica',
    ],
};

export const FemalePronouns: WordListWithoutId = {
    name: 'Female Pronouns',
    symbol: (
        <svg width={22} height={14}>
            <BsBracesAsterisk x={0} y={0} size={14} />
            <BsGenderFemale x={12} y={0} size={10} />
        </svg>
    ),
    words: ['she', 'herself', 'her', 'hers'],
};

export const FemaleStereotypes: WordListWithoutId = {
    name: 'Female Stereotypes',
    symbol: (
        <svg width={22} height={14}>
            <TbTargetOff x={0} y={0} size={14} />
            <BsGenderFemale x={12} y={0} size={10} />
        </svg>
    ),
    words: [
        'aerobics',
        'apparel',
        'assistant',
        'baker',
        'bathing',
        'beautiful',
        'beauty',
        'blonde',
        'bookkeeper',
        'ca',
        'cashier',
        'chatty',
        'cheerleader',
        'cheerleading',
        'clerk',
        'cocktail',
        'cooking',
        'counselor',
        'crafting',
        'cute',
        'dancer',
        'educator',
        'emotional',
        'flirt',
        'flirtatious',
        'flower',
        'gossip',
        'graceful',
        'hairdresser',
        'hairdryer',
        'homemaker',
        'hooker',
        'housekeeper',
        'housekeepers',
        'housework',
        'hula',
        'indoor',
        'jealousy',
        'jewelry',
        'kawaii',
        'laundering',
        'librarian',
        'librarians',
        'lotion',
        'lovely',
        'marvelous',
        'mirror',
        'moisturizer',
        'nanny',
        'neat',
        'nurse',
        'nursery',
        'nurses',
        'nurturing',
        'parenting',
        'passive',
        'pink',
        'pretty',
        'receptionist',
        'ribbon',
        'romance',
        'romantic',
        'secretary',
        'selfie',
        'server',
        'sew',
        'sewing',
        'shopping',
        'smoothie',
        'soft',
        'softball',
        'stylist',
        'submissive',
        'sweet',
        'tailor',
        'tall',
        'teacher',
        'thin',
        'violinist',
        'waiter',
        'weak',
        'yoga',
        'hysterical',
        'makeup',
    ],
};

export const Intelligence: WordListWithoutId = {
    name: 'Intelligence',
    symbol: <BiBrain size={14} />,
    words: [
        'precocious',
        'resourceful',
        'inquisitive',
        'genius',
        'inventive',
        'astute',
        'adaptable',
        'reflective',
        'discerning',
        'intuitive',
        'inquiring',
        'judicious',
        'analytical',
        'apt',
        'venerable',
        'imaginative',
        'shrewd',
        'thoughtful',
        'wise',
        'smart',
        'ingenious',
        'clever',
        'brilliant',
        'logical',
        'intelligent',
    ],
};

export const Insults: WordListWithoutId = {
    name: 'Insults',
    symbol: <BsExplicit size={14} />,
    words: [
        'ass',
        'asshole',
        'bastard',
        'bitch',
        'dick',
        'douche',
        'douchebag',
        'fucker',
        'fuckface',
        'jerk',
        'shithead',
        'arse',
        'bloody',
        'bugger',
        'cow',
        'crap',
        'damn',
        'ginger',
        'git',
        'god',
        'goddam',
        'jesus christ',
        'minger',
        'sod-off',
        'arsehole',
        'balls',
        'bollocks',
        'bullshit',
        'feck',
        'munter',
        'pissed',
        'pissed off',
        'shit',
        'son of a bitch',
        'tits',
        'beaver',
        'beef curtains',
        'bellend',
        'bloodclaat',
        'clunge',
        'cock',
        'dick',
        'dickhead',
        'fanny',
        'flaps',
        'gash',
        'knob',
        'minge',
        'prick',
        'punani',
        'pussy',
        'snatch',
        'twat',
    ],
};

export const MaleNouns: WordListWithoutId = {
    name: 'Male Nouns',
    symbol: (
        <svg width={22} height={14}>
            <MdOutlineLabel x={0} y={0} size={14} />
            <BsGenderMale x={12} y={0} size={10} />
        </svg>
    ),
    words: [
        'actor',
        'actors',
        'airman',
        'airmen',
        'uncle',
        'uncles',
        'boy',
        'boys',
        'groom',
        'grooms',
        'brother',
        'brothers',
        'businessman',
        'businessmen',
        'chairman',
        'chairmen',
        'dude',
        'dudes',
        'dad',
        'dads',
        'daddy',
        'daddies',
        'son',
        'sons',
        'male',
        'males',
        'guy',
        'guys',
        'father',
        'fathers',
        'grandson',
        'grandsons',
        'gal',
        'gals',
        'gentleman',
        'gentlemen',
        'he',
        'himself',
        'him',
        'his',
        'husband',
        'husbands',
        'king',
        'kings',
        'lord',
        'lords',
        'man',
        'men',
        'sir',
        'mr.',
        'policeman',
        'cameramen',
        'prince',
        'princes',
        'spokesman',
        'spokesmen',
        'cowboy',
        'cowboys',
        'cameraman',
        'busboy',
        'busboys',
        'bellboy',
        'bellboys',
        'barman',
        'barmen',
        'tailor',
        'tailors',
        'governor',
        'governors',
        'adultor',
        'adultors',
        'god',
        'gods',
        'host',
        'hosts',
        'abbot',
        'abbots',
        'bachelor',
        'bachelors',
        'baron',
        'barons',
        'beau',
        'beaus',
        'bridegroom',
        'bridegrooms',
        'duke',
        'dukes',
        'emperor',
        'emperors',
        'enchanter',
        'fiance',
        'fiances',
        'priest',
        'priests',
        'grandfather',
        'grandfathers',
        'headmaster',
        'headmasters',
        'hero',
        'heros',
        'lad',
        'lads',
        'landlord',
        'landlords',
        'manservant',
        'manservants',
        'marquis',
        'masseur',
        'masseurs',
        'master',
        'masters',
        'monk',
        'monks',
        'nephew',
        'nephews',
        'sorcerer',
        'sorcerers',
        'stepfather',
        'stepfathers',
        'stepson',
        'stepsons',
        'steward',
        'stewards',
        'waiter',
        'waiters',
        'widower',
        'widowers',
        'wizard',
        'wizards',
    ],
};

export const MaleOccupations: WordListWithoutId = {
    name: 'Male Occupations',
    symbol: (
        <svg width={22} height={14}>
            <BsBriefcase x={0} y={0} size={14} />
            <BsGenderMale x={12} y={0} size={10} />
        </svg>
    ),
    words: [
        'salesperson',
        'director ofreligious activities',
        'crossing guard',
        'photographer',
        'lifeguard',
        'lodging manager',
        'healthcare practitioner',
        'sales agent',
        'mail clerk',
        'electrical assembler',
        'insurance sales agent',
        'insurance underwriter',
        'medical scientist',
        'statistician',
        'training specialist',
        'judge',
        'bartender',
        'dispatcher',
        'order clerk',
        'mail sorter',
        'baker',
        'butcher',
        'plumber',
        'pilot',
        'accountant',
        'mechanic',
        'cabinetmaker',
        'actor',
        'carpenter',
        'inspector',
        'administrator',
        'designer',
        'upholsterer',
        'grader',
        'dentist',
        'musician',
        'barber',
        'writer',
        'electrician',
        'geologist',
        'guard',
        'manager',
        'sheriff',
        'supervisor',
        'counselor',
        'firefighter',
        'machinist',
        'farmer',
        'tailor',
        'engineer',
        'assembler',
        'janitor',
        'pilot',
        'gardener',
        'machinist',
        'lawyer',
        'doctor',
        'player',
        'quarterback',
    ],
};

export const MalePersonNames: WordListWithoutId = {
    name: 'Male Person Names',
    symbol: (
        <svg width={22} height={14}>
            <BsCardHeading x={0} y={0} size={14} />
            <BsGenderMale x={12} y={0} size={10} />
        </svg>
    ),
    words: [
        'liam',
        'noah',
        'oliver',
        'william',
        'elijah',
        'james',
        'benjamin',
        'lucas',
        'mason',
        'alexander',
        'henry',
        'jacob',
        'michael',
        'daniel',
        'logan',
        'jackson',
        'sebastian',
        'jack',
        'aiden',
        'owen',
        'samuel',
        'matthew',
        'joseph',
        'levi',
        'mateo',
        'david',
        'john',
        'wyatt',
        'carter',
        'julian',
        'luke',
        'grayson',
        'isaac',
        'jayden',
        'gabriel',
        'anthony',
        'dylan',
        'leo',
        'lincoln',
        'jaxon',
        'asher',
        'christopher',
        'josiah',
        'andrew',
        'thomas',
        'joshua',
        'ezra',
        'hudson',
        'charles',
        'isaiah',
        'nathan',
        'adrian',
        'christian',
        'maverick',
        'colton',
        'elias',
        'aaron',
        'eli',
        'landon',
        'nolan',
        'cameron',
        'connor',
        'jeremiah',
        'ezekiel',
        'easton',
        'miles',
        'robert',
        'jameson',
        'nicholas',
        'greyson',
        'cooper',
        'ian',
        'axel',
        'jaxson',
        'dominic',
        'leonardo',
        'luca',
        'jordan',
        'adam',
        'xavier',
        'jose',
        'jace',
        'everett',
        'declan',
        'evan',
        'kayden',
        'parker',
        'wesley',
        'kai',
        'ryan',
        'jonathan',
        'ronald',
    ],
};

export const MalePronouns: WordListWithoutId = {
    name: 'Male Pronouns',
    symbol: (
        <svg width={22} height={14}>
            <BsBracesAsterisk x={0} y={0} size={14} />
            <BsGenderMale x={12} y={0} size={10} />
        </svg>
    ),
    words: ['he', 'himself', 'him', 'his'],
};

export const MaleStereotypes: WordListWithoutId = {
    name: 'Male Stereotypes',
    symbol: (
        <svg width={22} height={14}>
            <TbTargetOff x={0} y={0} size={14} />
            <BsGenderMale x={12} y={0} size={10} />
        </svg>
    ),
    words: [
        'adventurer',
        'aggressive',
        'tycoon',
        'warrior',
        'ambitious',
        'trucker',
        'welder',
        'strong',
        'terrorist',
        'soldier',
        'astronomer',
        'sniper',
        'skipper',
        'banker',
        'baseball',
        'sergeant',
        'bodyguard',
        'boss',
        'boxer',
        'cabbie',
        'captain',
        'cardiologist',
        'carpenter',
        'ceo',
        'chairperson',
        'chancellor',
        'chef',
        'colonel',
        'commander',
        'conductor',
        'police',
        'custodian',
        'dentist',
        'detective',
        'diplomat',
        'doctor',
        'driving',
        'drummer',
        'economist',
        'electrician',
        'engineer',
        'engineering',
        'entrepreneur',
        'financier',
        'firefighter',
        'footballer',
        'gambler',
        'gamer',
        'gangster',
        'geek',
        'geeks',
        'gentle',
        'guitarist',
        'industrialist',
        'inventor',
        'investigator',
        'laborer',
        'lawyer',
        'leader',
        'lieutenant',
        'lifeguard',
        'magistrate',
        'manager',
        'marshal',
        'mathematician',
        'mechanic',
        'muscle',
        'muscular',
        'owner',
        'philosopher',
        'physicist',
        'pilot',
        'plumber',
        'politician',
        'president',
        'professor',
        'programmer',
        'rugby',
        'sailor',
        'science',
        'scientist',
        'sculptor',
        'blue',
        'football',
    ],
};

export const NegativeCharacteristics: WordListWithoutId = {
    name: 'Negative Characteristics',
    symbol: <MdOutlineSentimentDissatisfied size={14} />,
    words: [
        'Aggressive',
        'Angry',
        'Anxious',
        'Argumentative',
        'Arrogant',
        'Bored',
        'Bossy',
        'Brutal',
        'Careless',
        'Charmless',
        'Clumsy',
        'Conceited',
        'Cowardly',
        'Critical',
        'Cruel',
        'Dangerous',
        'Deceitful',
        'Destructive',
        'Devious',
        'Difficult',
        'Discouraging',
        'Discourteous',
        'Dishonest',
        'Disloyal',
        'Disobedient',
        'Disorganized',
        'Disrespectful',
        'Disruptive',
        'Envious',
        'Fearful',
        'Foolish',
        'Forgetful',
        'Frightening',
        'Gloomy',
        'Greedy',
        'Grim',
        'Hateful',
        'Haughty',
        'Hostile',
        'Ignorant',
        'Impatient',
        'Impractical',
        'Inconsiderate',
        'Insincere',
        'Insulting',
        'Intolerant',
        'Irresponsible',
        'Irritable',
        'Jealous',
        'Lazy',
        'Liar',
        'Mean',
        'Meddlesome',
        'Messy',
        'Miserable',
        'Monstrous',
        'Moody',
        'Negative',
        'Neglectful',
        'Obnoxious',
        'Petty',
        'Possessive',
        'Power-hungry',
        'Prejudiced',
        'Resentful',
        'Rude',
        'Scornful',
        'Selfish',
        'Shallow',
        'Sloppy',
        'Sneaky',
        'Snobbish',
        'Thoughtless',
        'Unappreciative',
        'Uncaring',
        'Uncooperative',
        'Unforgiving',
        'Unfriendly',
        'Ungrateful',
        'Unhealthy',
        'Unreliable',
        'Violent',
        'Weak',
        'Wicked',
    ],
};

export const Occupations: WordListWithoutId = {
    name: 'Occupations',
    symbol: <BsBriefcase size={14} />,
    words: [
        'Accountant',
        'Accounts Assistant',
        'Accounts Clerk',
        'Accounts Manager',
        'Accounts Staff',
        'Acoustic Engineer',
        'Actor',
        'Actress',
        'Actuary',
        'Acupuncturist',
        'Adjustor',
        'Administration Assistant',
        'Administration Clerk',
        'Administration Manager',
        'Administration Staff',
        'Administrator',
        'Advertising Agent',
        'Advertising Assistant',
        'Advertising Clerk',
        'Advertising Contractor',
        'Advertising Executive',
        'Advertising Manager',
        'Advertising Staff',
        'Aerial Erector',
        'Aerobic Instructor',
        'Aeronautical Engineer',
        'Agent',
        'Air Traffic Controller',
        'Aircraft Designer',
        'Aircraft Engineer',
        'Aircraft Maintenance Engineer',
        'Aircraft Surface Finisher',
        'Airman',
        'Airport Controller',
        'Airport Manager',
        'Almoner',
        'Ambulance Controller',
        'Ambulance Crew',
        'Ambulance Driver',
        'Amusement Arcade Worker',
        'Anaesthetist',
        'Analyst',
        'Analytical Chemist',
        'Animal Breeder',
        'Anthropologist',
        'Antique Dealer',
        'Applications Engineer',
        'Applications Programmer',
        'Arbitrator',
        'Arborist',
        'Archaeologist',
        'Architect',
        'Archivist',
        'Area Manager',
        'Armourer',
        'Aromatherapist',
        'Art Critic',
        'Art Dealer',
        'Art Historian',
        'Art Restorer',
        'Artexer',
        'Artist',
        'Arts',
        'Assembly Worker',
        'Assessor',
        'Assistant',
        'Assistant Caretaker',
        'Assistant Cook',
        'Assistant Manager',
        'Assistant Nurse',
        'Assistant Teacher',
        'Astrologer',
        'Astronomer',
        'Attendant',
        'Au Pair',
        'Auction Worker',
        'Auctioneer',
        'Audiologist',
        'Audit Clerk',
        'Audit Manager',
        'Auditor',
        'Auto Electrician',
        'Auxiliary Nurse',
        'Bacon Curer',
        'Baggage Handler',
        'Bailiff',
        'Baker',
        'Bakery Assistant',
        'Bakery Manager',
        'Bakery Operator',
        'Balloonist',
        'Bank Clerk',
        'Bank Manager',
        'Bank Messenger',
        'Baptist Minister',
        'Bar Manager',
        'Bar Steward',
        'Barber',
        'Barmaid',
        'Barman',
        'Barrister',
        'Beautician',
        'Beauty Therapist',
        'Betting Shop',
        'Bill Poster',
        'Bingo Caller',
        'Biochemist',
        'Biologist',
        'Blacksmith',
        'Blind Assembler',
        'Blind Fitter',
        'Blinds Installer',
        'Boat Builder',
        'Body Fitter',
        'Bodyguard',
        'Bodyshop',
        'Book Binder',
        'Book Seller',
        'Book-Keeper',
        'Booking Agent',
        'Booking Clerk',
        'Bookmaker',
        'Botanist',
        'Branch Manager',
        'Breeder',
        'Brewer',
        'Brewery Manager',
        'Brewery Worker',
        'Bricklayer',
        'Broadcaster',
        'Builder',
        'Builders Labourer',
        'Building Advisor',
        'Building Control',
        'Building Engineer',
        'Building Estimator',
        'Building Foreman',
        'Building Inspector',
        'Building Manager',
        'Building Surveyor',
        'Bursar',
        'Bus Company',
        'Bus Conductor',
        'Bus Driver',
        'Bus Mechanic',
        'Bus Valeter',
        'Business Consultant',
        'Business Proprietor',
        'Butcher',
        'Butchery Manager',
        'Butler',
        'Buyer',
        'Cab Driver',
        'Cabinet Maker',
        'Cable Contractor',
        'Cable Jointer',
        'Cable TV Installer',
        'Cafe Owner',
        'Cafe Staff',
        'Cafe Worker',
        'Calibration Manager',
        'Camera Repairer',
        'Cameraman',
        'Car Dealer',
        'Car Delivery Driver',
        'Car Park Attendant',
        'Car Salesman',
        'Car Valet',
        'Car Wash Attendant',
        'Care Assistant',
        'Care Manager',
        'Careers Advisor',
        'Careers Officer',
        'Caretaker',
        'Cargo Operator',
        'Carpenter',
        'Carpet Cleaner',
        'Carpet Fitter',
        'Carpet Retailer',
        'Carphone Fitter',
        'Cartographer',
        'Cartoonist',
        'Cashier',
        'Casual Worker',
        'Caterer',
        'Catering Consultant',
        'Catering Manager',
        'Catering Staff',
        'Caulker',
        'Ceiling Contractor',
        'Ceiling Fixer',
        'Cellarman',
        'Chambermaid',
        'Chandler',
        'Chaplain',
        'Charge Hand',
        'Charity Worker',
        'Chartered',
        'Chartered Accountant',
        'Chauffeur',
        'Chef',
        'Chemist',
        'Chicken Chaser',
        'Child Minder',
        'Childminder',
        'Chimney Sweep',
        'China Restorer',
        'Chiropodist',
        'Chiropractor',
        'Choreographer',
        'Church Officer',
        'Church Warden',
        'Cinema Manager',
        'Circus Proprietor',
        'Circus Worker',
        'Civil Engineer',
        'Civil Servant',
        'Claims Adjustor',
        'Claims Assessor',
        'Claims Manager',
        'Clairvoyant',
        'Classroom Aide',
        'Cleaner',
        'Clergyman',
        'Cleric',
        'Clerk',
        'Commissioned',
        'Consultant',
        'Coroner',
        'Councillor',
        'Counsellor',
        'Dealer',
        'Decorator',
        'Delivery Driver',
        'Doctor',
        'Driver',
        'Economist',
        'Editor',
        'Employee',
        'Employment',
        'Engineer',
        'English Teacher',
        'Entertainer',
        'Envoy',
        'Executive',
        'Farmer',
        'Fireman',
        'Floor Layer',
        'Floor Manager',
        'Florist',
        'Flour Miller',
        'Flower Arranger',
        'Flying Instructor',
        'Foam Convertor',
        'Food Processor',
        'Footballer',
        'Foreman',
        'Forensic Scientist',
        'Forest Ranger',
        'Forester',
        'Fork Lift Truck Driver',
        'Forwarding Agent',
        'Foster Parent',
        'Foundry Worker',
        'Fraud Investigator',
        'French Polisher',
        'Fruiterer',
        'Fuel Merchant',
        'Fund Raiser',
        'Funeral Director',
        'Funeral Furnisher',
        'Furnace Man',
        'Furniture Dealer',
        'Furniture Remover',
        'Furniture Restorer',
        'Furrier',
        'Gallery Owner',
        'Gambler',
        'Gamekeeper',
        'Gaming Board Inspector',
        'Gaming Club Manager',
        'Gaming Club Proprietor',
        'Garage Attendant',
        'Garage Foreman',
        'Garage Manager',
        'Garda',
        'Garden Designer',
        'Gardener',
        'Gas Fitter',
        'Gas Mechanic',
        'Gas Technician',
        'Gate Keeper',
        'Genealogist',
        'General Practitioner',
        'Geologist',
        'Geophysicist',
        'Gilder',
        'Glass Worker',
        'Glazier',
        'Goldsmith',
        'Golf Caddy',
        'Golf Club Professional',
        'Golfer',
        'Goods Handler',
        'Governor',
        'Granite Technician',
        'Graphic Designer',
        'Graphologist',
        'Grave Digger',
        'Gravel Merchant',
        'Green Keeper',
        'Greengrocer',
        'Grocer',
        'Groom',
        'Ground Worker',
        'Groundsman',
        'Guest House Owner',
        'Guest House Proprietor',
        'Gun Smith',
        'Gynaecologist',
        'HGV Driver',
        'HGV Mechanic',
        'Hairdresser',
        'Handyman',
        'Hardware Dealer',
        'Haulage Contractor',
        'Hawker',
        'Health Advisor',
        'Health And Safety',
        'Health Care Assistant',
        'Health Consultant',
        'Health Nurse',
        'Health Planner',
        'Health Service',
        'Health Therapist',
        'Health Visitor',
        'Hearing Therapist',
        'Heating Engineer',
        'Herbalist',
        'Highway Inspector',
        'Hire Car Driver',
        'Historian',
        'History Teacher',
        'Hod Carrier',
        'Home Economist',
        'Home Help',
        'Homecare Manager',
        'Homeopath',
        'Homeworker',
        'Hop Merchant',
        'Horse Breeder',
        'Horse Dealer',
        'Horse Riding Instructor',
        'Horse Trader',
        'Horse Trainer',
        'Horticultural Consultant',
        'Horticulturalist',
        'Hosiery Mechanic',
        'Hosiery Worker',
        'Hospital Consultant',
        'Hospital Doctor',
        'Hospital Manager',
        'Hospital Orderly',
        'Hospital Technician',
        'Hospital Warden',
        'Hospital Worker',
        'Hostess',
        'Hot Foil Printer',
        'Hotel Consultant',
        'Hotel Worker',
        'Hotelier',
        'Househusband',
        'Housekeeper',
        'Housewife',
        'Housing Assistant',
        'Housing Officer',
        'Housing Supervisor',
        'Hygienist',
        'Hypnotherapist',
        'Hypnotist',
        'IT Consultant',
        'IT Manager',
        'IT Trainer',
        'Ice Cream Vendor',
        'Illustrator',
        'Immigration Officer',
        'Import Consultant',
        'Importer',
        'Independent Means',
        'Induction Moulder',
        'Industrial Chemist',
        'Industrial Consultant',
        'Injection Moulder',
        'Inspector',
        'Instructor',
        'Instrument Engineer',
        'Instrument Maker',
        'Instrument Supervisor',
        'Instrument Technician',
        'Insurance Agent',
        'Insurance Assessor',
        'Insurance Broker',
        'Insurance Consultant',
        'Insurance Inspector',
        'Insurance Staff',
        'Interior Decorator',
        'Interior Designer',
        'Interpreter',
        'Interviewer',
        'Inventor',
        'Investigator',
        'Investment Advisor',
        'Investment Banker',
        'Investment Manager',
        'Investment Strategist',
        'Ironmonger',
        'Janitor',
        'Jazz Composer',
        'Jeweller',
        'Jewellery',
        'Jockey',
        'Joiner',
        'Joinery Consultant',
        'Journalist',
        'Judge',
        'Keep Fit Instructor',
        'Kennel Hand',
        'Kitchen Worker',
        'Knitter',
        'Labelling Operator',
        'Laboratory Analyst',
        'Labourer',
        'Laminator',
        'Lampshade Maker',
        'Land Agent',
        'Land Surveyor',
        'Landlady',
        'Landlord',
        'Landowner',
        'Landworker',
        'Lathe Operator',
        'Laundry Staff',
        'Laundry Worker',
        'Lavatory Attendant',
        'Law Clerk',
        'Lawn Mower',
        'Lawyer',
        'Leaflet Distributor',
        'Leather Worker',
        'Lecturer',
        'Ledger Clerk',
        'Legal Advisor',
        'Legal Assistant',
        'Legal Executive',
        'Legal Secretary',
        'Letting Agent',
        'Liaison Officer',
        'Librarian',
        'Library Manager',
        'Licensed Premises',
        'Licensee',
        'Licensing',
        'Lifeguard',
        'Lift Attendant',
        'Lift Engineer',
        'Lighterman',
        'Lighthouse Keeper',
        'Lighting Designer',
        'Lighting Technician',
        'Lime Kiln Attendant',
        'Line Manager',
        'Line Worker',
        'Lineman',
        'Linguist',
        'Literary Agent',
        'Literary Editor',
        'Lithographer',
        'Litigation Manager',
        'Loans Manager',
        'Local Government',
        'Lock Keeper',
        'Locksmith',
        'Locum Pharmacist',
        'Log Merchant',
        'Lorry Driver',
        'Loss Adjustor',
        'Loss Assessor',
        'Lumberjack',
        'Machine Fitters',
        'Machine Minder',
        'Machine Operator',
        'Machine Setter',
        'Machine Tool',
        'Machine Tool Fitter',
        'Machinist',
        'Magician',
        'Magistrate',
        'Magistrates Clerk',
        'Maid',
        'Maintenance Fitter',
        'Make Up Artist',
        'Manicurist',
        'Manufacturing',
        'Map Mounter',
        'Marble Finisher',
        'Marble Mason',
        'Marine Broker',
        'Marine Consultant',
        'Marine Electrician',
        'Marine Engineer',
        'Marine Geologist',
        'Marine Pilot',
        'Marine Surveyor',
        'Market Gardener',
        'Market Research',
        'Market Researcher',
        'Market Trader',
        'Marketing Agent',
        'Marketing Assistant',
        'Marketing Coordinator',
        'Marketing Director',
        'Marketing Manager',
        'Marquee Erector',
        'Massage Therapist',
        'Masseur',
        'Masseuse',
        'Master Mariner',
        'Materials Controller',
        'Materials Manager',
        'Mathematician',
        'Maths Teacher',
        'Matron',
        'Mattress Maker',
        'Meat Inspector',
        'Meat Wholesaler',
        'Mechanic',
        'Medal Dealer',
        'Medical Advisor',
        'Medical Assistant',
        'Medical Consultant',
        'Medical Officer',
        'Medical Physicist',
        'Medical Practitioner',
        'Medical Researcher',
        'Medical Secretary',
        'Medical Student',
        'Medical Supplier',
        'Medical Technician',
        'Merchandiser',
        'Merchant',
        'Merchant Banker',
        'Merchant Seaman',
        'Messenger',
        'Metal Dealer',
        'Metal Engineer',
        'Metal Polisher',
        'Metal Worker',
        'Metallurgist',
        'Meteorologist',
        'Meter Reader',
        'Microbiologist',
        'Midwife',
        'Military Leader',
        'Milklady',
        'Milkman',
        'Mill Operator',
        'Mill Worker',
        'Miller',
        'Milliner',
        'Millwright',
        'Miner',
        'Mineralologist',
        'Minibus Driver',
        'Minicab Driver',
        'Mining Consultant',
        'Mining Engineer',
        'Money Broker',
        'Moneylender',
        'Mooring Contractor',
        'Mortgage Broker',
        'Mortician',
        'Motor Dealer',
        'Motor Engineer',
        'Motor Fitter',
        'Motor Mechanic',
        'Motor Racing',
        'Motor Trader',
        'Museum Assistant',
        'Museum Attendant',
        'Music Teacher',
        'Musician',
        'Nanny',
        'Navigator',
        'Negotiator',
        'Neurologist',
        'Newsagent',
        'Night Porter',
        'Night Watchman',
        'Nuclear Scientist',
        'Nun',
        'Nurse',
        'Nursery Assistant',
        'Nursery Nurse',
        'Nursery Worker',
        'Nurseryman',
        'Nursing Assistant',
        'Nursing Auxiliary',
        'Nursing Manager',
        'Nursing Sister',
        'Nutritionist',
        'Off Shore',
        'Office Manager',
        'Office Worker',
        'Oil Broker',
        'Oil Rig Crew',
        'Opera Singer',
        'Operations',
        'Operative',
        'Operator',
        'Optical',
        'Optical Advisor',
        'Optical Assistant',
        'Optician',
        'Optometrist',
        'Orchestral',
        'Organiser',
        'Organist',
        'Ornamental',
        'Ornithologist',
        'Orthopaedic',
        'Orthoptist',
        'Osteopath',
        'Outdoor Pursuits',
        'Outreach Worker',
        'Packaging',
        'Packer',
        'Paediatrician',
        'Paint Consultant',
        'Painter',
        'Palaeobotanist',
        'Palaeontologist',
        'Pallet Maker',
        'Panel Beater',
        'Paramedic',
        'Park Attendant',
        'Park Keeper',
        'Park Ranger',
        'Partition Erector',
        'Parts Man',
        'Parts Manager',
        'Parts Supervisor',
        'Party Planner',
        'Pasteuriser',
        'Pastry Chef',
        'Patent Agent',
        'Patent Attorney',
        'Pathologist',
        'Patrolman',
        'Pattern Cutter',
        'Pattern Maker',
        'Pattern Weaver',
        'Pawnbroker',
        'Payroll Assistant',
        'Payroll Clerk',
        'Payroll Manager',
        'Payroll Supervisor',
        'Personnel Officer',
        'Pest Controller',
        'Pet Minder',
        'Pharmacist',
        'Philatelist',
        'Photographer',
        'Physician',
        'Physicist',
        'Physiologist',
        'Physiotherapist',
        'Piano Teacher',
        'Piano Tuner',
        'Picture Editor',
        'Picture Framer',
        'Picture Reseacher',
        'Pig Man',
        'Pig Manager',
        'Pilot',
        'Pipe Fitter',
        'Pipe Inspector',
        'Pipe Insulator',
        'Pipe Layer',
        'Planning Engineer',
        'Planning Manager',
        'Planning Officer',
        'Planning Technician',
        'Plant Attendant',
        'Plant Driver',
        'Plant Engineer',
        'Plant Fitter',
        'Plant Manager',
        'Plant Operator',
        'Plasterer',
        'Plastics Consultant',
        'Plastics Engineer',
        'Plate Layer',
        'Plater',
        'Playgroup Assistant',
        'Playgroup Leader',
        'Plumber',
        'Podiatrist',
        'Police Officer',
        'Polisher',
        'Pool Attendant',
        'Pools Collector',
        'Porter',
        'Portfolio Manager',
        'Post Sorter',
        'Postman',
        'Postmaster',
        'Postwoman',
        'Potter',
        'Practice Manager',
        'Preacher',
        'Precision Engineer',
        'Premises',
        'Premises Security',
        'Press Officer',
        'Press Operator',
        'Press Setter',
        'Presser',
        'Priest',
        'Print Finisher',
        'Printer',
        'Prison Chaplain',
        'Prison Officer',
        'Private Investigator',
        'Probation Officer',
        'Probation Worker',
        'Procurator Fiscal',
        'Produce Supervisor',
        'Producer',
        'Product Installer',
        'Product Manager',
        'Production Engineer',
        'Production Hand',
        'Production Manager',
        'Production Planner',
        'Professional Boxer',
        'Professional Racing',
        'Professional Wrestler',
        'Progress Chaser',
        'Progress Clerk',
        'Project Co-ordinator',
        'Project Engineer',
        'Project Leader',
        'Project Manager',
        'Project Worker',
        'Projectionist',
        'Promoter',
        'Proof Reader',
        'Property Buyer',
        'Property Dealer',
        'Property Developer',
        'Property Manager',
        'Property Valuer',
        'Proprietor',
        'Psychiatrist',
        'Psychoanalyst',
        'Psychologist',
        'Psychotherapist',
        'Public House Manager',
        'Public Relations Of?cer',
        'Publican',
        'Publicity Manager',
        'Publisher',
        'Publishing Manager',
        'Purchase Clerk',
        'Purchase Ledger Clerk',
        'Purchasing Assistant',
        'Purchasing Manager',
        'Purser',
        'Quality Controller',
        'Quality Engineer',
        'Quality Inspector',
        'Quality Manager',
        'Quality Technician',
        'Quantity Surveyor',
        'Quarry Worker',
        'Racehorse Groom',
        'Racing Organiser',
        'Radio Controller',
        'Radio Director',
        'Radio Engineer',
        'Radio Operator',
        'Radio Presenter',
        'Radio Producer',
        'Radiographer',
        'Radiologist',
        'Rally Driver',
        'Receptionist',
        'Recorder',
        'Records Supervisor',
        'Recovery Vehicle Coordinator',
        'Recreational',
        'Recruitment Consultant',
        'Rector',
        'Reflexologist',
        'Refractory Engineer',
        'Refrigeration Engineer',
        'Refuse Collector',
        'Registrar',
        'Regulator',
        'Relocation Agent',
        'Remedial Therapist',
        'Rent Collector',
        'Rent Offcer',
        'Repair Man',
        'Repairer',
        'Reporter',
        'Representative',
        'Reprographic Assistant',
        'Research Analyst',
        'Research Consultant',
        'Research Director',
        'Research Scientist',
        'Research Technician',
        'Researcher',
        'Resin Caster',
        'Restaurant Manager',
        'Restaurateur',
        'Restorer',
        'Retired',
        'Revenue Clerk',
        'Revenue Officer',
        'Riding Instructor',
        'Rig Worker',
        'Rigger',
        'Riveter',
        'Road Safety Officer',
        'Road Sweeper',
        'Road Worker',
        'Roadworker',
        'Roof Tiler',
        'Roofer',
        'Rose Grower',
        'Royal Marine',
        'Rug Maker',
        'Saddler',
        'Safety Officer',
        'Sail Maker',
        'Sales Administrator',
        'Sales Assistant',
        'Sales Director',
        'Sales Engineer',
        'Sales Executive',
        'Sales Manager',
        'Sales Representative',
        'Sales Support',
        'Salesman',
        'Saleswoman',
        'Sand Blaster',
        'Saw Miller',
        'Scaffolder',
        'School Crossing',
        'School Inspector',
        'Scientific Officer',
        'Scientist',
        'Scrap Dealer',
        'Screen Printer',
        'Screen Writer',
        'Script Writer',
        'Sculptor',
        'Seaman',
        'Seamstress',
        'Secretary',
        'Security Consultant',
        'Security Controller',
        'Security Guard',
        'Security Officer',
        'Servant',
        'Service Engineer',
        'Service Manager',
        'Share Dealer',
        'Sheet Metal Worker',
        'Shelf Filler',
        'Shelter Warden',
        'Shepherd',
        'Sheriff',
        'Sheriff Clerk',
        'Sheriff Principal',
        'Shift Controller',
        'Ship Broker',
        'Ship Builder',
        'Shipping Clerk',
        'Shipping Officer',
        'Shipwright',
        'Shipyard Worker',
        'Shoe Maker',
        'Shoe Repairer',
        'Shooting Instructor',
        'Shop Assistant',
        'Shop Fitter',
        'Shop Keeper',
        'Shop Manager',
        'Shop Proprietor',
        'Shot Blaster',
        'Show Jumper',
        'Showman',
        'Shunter',
        'Sign Maker',
        'Signalman',
        'Signwriter',
        'Site Agent',
        'Site Engineer',
        'Skipper',
        'Slater',
        'Slaughterman',
        'Smallholder',
        'Social Worker',
        'Software Consultant',
        'Software Engineer',
        'Soldier',
        'Solicitor',
        'Song Writer',
        'Sound Artist',
        'Sound Engineer',
        'Sound Technician',
        'Special Constable',
        'Special Needs',
        'Speech Therapist',
        'Sports Administrator',
        'Sports Coach',
        'Sports Commentator',
        'Sportsman',
        'Sportsperson',
        'Sportswoman',
        'Spring Maker',
        'Stable Hand',
        'Staff Nurse',
        'Stage Director',
        'Stage Hand',
        'Stage Manager',
        'Stage Mover',
        'Station Manager',
        'Stationer',
        'Statistician',
        'Steel Erector',
        'Steel Worker',
        'Steeplejack',
        'Stenographer',
        'Steward',
        'Stewardess',
        'Stock Controller',
        'Stock Manager',
        'Stockbroker',
        'Stockman',
        'Stocktaker',
        'Stone Cutter',
        'Stone Sawyer',
        'Stonemason',
        'Store Detective',
        'Storeman',
        'Storewoman',
        'Street Entertainer',
        'Street Trader',
        'Stud Hand',
        'Student',
        'Student Nurse',
        'Student Teacher',
        'Studio Manager',
        'Sub-Postmaster',
        'Sub-Postmistress',
        'Supervisor',
        'Supply Teacher',
        'Surgeon',
        'Surveyor',
        'Systems Analyst',
        'Systems Engineer',
        'Systems Manager',
        'TV Editor',
        'Tachograph Analyst',
        'Tacker',
        'Tailor',
        'Tank Farm Operative',
        'Tanker Driver',
        'Tanner',
        'Tattooist',
        'Tax Advisor',
        'Tax Analyst',
        'Tax Assistant',
        'Tax Consultant',
        'Tax Inspector',
        'Tax Manager',
        'Tax Officer',
        'Taxi Controller',
        'Taxi Driver',
        'Taxidermist',
        'Tea Blender',
        'Tea Taster',
        'Teacher',
        'Teachers Assistant',
        'Technical Advisor',
        'Technical Analyst',
        'Technical Assistant',
        'Technical Author',
        'Technical Clerk',
        'Technical Co-ordinator',
        'Technical Director',
        'Technical Editor',
        'Technical Engineer',
        'Technical Illustrator',
        'Technical Instructor',
        'Technical Liaison',
        'Technical Manager',
        'Technician',
        'Telecommunication',
        'Telecommunications',
        'Telegraphist',
        'Telemarketeer',
        'Telephone Engineer',
        'Telephonist',
        'Telesales Person',
        'Television Director',
        'Television Engineer',
        'Television Presenter',
        'Television Producer',
        'Telex Operator',
        'Temperature Time',
        'Tennis Coach',
        'Textile Consultant',
        'Textile Engineer',
        'Textile Technician',
        'Textile Worker',
        'Thatcher',
        'Theatre Manager',
        'Theatre Technician',
        'Theatrical Agent',
        'Therapist',
        'Thermal Engineer',
        'Thermal Insulator',
        'Ticket Agent',
        'Ticket Inspector',
        'Tiler',
        'Timber Inspector',
        'Timber Worker',
        'Tobacconist',
        'Toll Collector',
        'Tool Maker',
        'Tour Agent',
        'Tour Guide',
        'Town Clerk',
        'Town Planner',
        'Toy Maker',
        'Toy Trader',
        'Track Worker',
        'Tractor Driver',
        'Tractor Mechanic',
        'Trade Mark Agent',
        'Trade Union Official',
        'Trading Standards',
        'Traffic Warden',
        'Train Driver',
        'Trainee Manager',
        'Training Advisor',
        'Training Assistant',
        'Training Co-ordinator',
        'Training Consultant',
        'Training Instructor',
        'Training Manager',
        'Training Officer',
        'Transcriber',
        'Translator',
        'Transport Clerk',
        'Transport Consultant',
        'Transport Controller',
        'Transport Engineer',
        'Transport Manager',
        'Transport Officer',
        'Transport Planner',
        'Travel Agent',
        'Travel Clerk',
        'Travel Consultant',
        'Travel Courier',
        'Travel Guide',
        'Travel Guide Writer',
        'Travel Representative',
        'Travelling Showman',
        'Treasurer',
        'Tree Feller',
        'Tree Surgeon',
        'Trichologist',
        'Trinity House Pilot',
        'Trout Farmer',
        'Tug Skipper',
        'Tunneller',
        'Turf Accountant',
        'Turkey Farmer',
        'Turner',
        'Tutor',
        'Typesetter',
        'Typewriter Engineer',
        'Typist',
        'Tyre Builder',
        'Tyre Fitter',
        'Tyre Inspector',
        'Tyre Technician',
        'Undertaker',
        'Underwriter',
        'Upholsterer',
        'Valuer',
        'Valve Technician',
        'Van Driver',
        'Vehicle Assessor',
        'Vehicle Body Worker',
        'Vehicle Engineer',
        'Vehicle Technician',
        'Ventriloquist',
        'Verger',
        'Veterinary Surgeon',
        'Vicar',
        'Video Artist',
        'Violin Maker',
        'Violinist',
        'Voluntary Worker',
        'Wages Clerk',
        'Waiter',
        'Waitress',
        'Warden',
        'Warehouse Manager',
        'Warehouseman',
        'Warehousewoman',
        'Watchmaker',
        'Weaver',
        'Weighbridge Clerk',
        'Weighbridge Operator',
        'Welder',
        'Welfare Assistant',
        'Welfare Officer',
        'Welfare Rights Officer',
        'Wheel Clamper',
        'Wholesale Newspaper',
        'Window Cleaner',
        'Window Dresser',
        'Windscreen Fitter',
        'Wine Merchant',
        'Wood Carver',
        'Wood Cutter',
        'Wood Worker',
        'Word Processing Operator',
        'Works Manager',
        'Writer',
        'Yacht Master',
        'Yard Manager',
        'Youth Hostel Warden',
        'Youth Worker',
        'Zoo Keeper',
        'Zoo Manager',
        'Zoologist',
    ],
};

export const PoorestCountries: WordListWithoutId = {
    name: 'Poorest Countries',
    symbol: <MdMoneyOff size={14} />,
    words: [
        'Greece',
        'Latvia',
        'Turkey',
        'Romania',
        'Kazakhstan',
        'Croatia',
        'Russia',
        'Chile',
        'Panama',
        'Equatorial Guinea',
        'Antigua and Barbuda',
        'Uruguay',
        'Mauritius',
        'Bulgaria',
        'Iran',
        'Argentina',
        'Libya',
        'Montenegro',
        'Belarus',
        'Mexico',
        'Barbados',
        'Gabon',
        'Turkmenistan',
        'Thailand',
        'Azerbaijan',
        'Costa Rica',
        'Botswana',
        'Iraq',
        'China',
        'Maldives',
        'Dominican Republic',
        'Brazil',
        'Serbia',
        'Algeria',
        'North Macedonia',
        'Suriname',
        'Grenada',
        'Palau',
        'Lebanon',
        'Colombia',
        'Saint Lucia',
        'South Africa',
        'Peru',
        'Paraguay',
        'Bosnia and Herzegovina',
        'Mongolia',
        'Albania',
        'Sri Lanka',
        'Indonesia',
        'Tunisia',
        'St. Vincent & Grenadines',
        'Ecuador',
        'Egypt',
        'Georgia',
        'Namibia',
        'Dominica',
        'Armenia',
        'Fiji',
        'Bhutan',
        'Jordan',
        'Jamaica',
        'Ukraine',
        'Eswatini',
        'Belize',
        'Philippines',
        'Morocco',
        'Guyana',
        'Guatemala',
        'El Salvador',
        'Bolivia',
        'Timor-Leste',
        'India',
        'Laos',
        'Cabo Verde',
        'Uzbekistan',
        'Vietnam',
        'Angola',
        'Samoa',
        'Myanmar',
        'Tonga',
        'Nigeria',
        'Nicaragua',
        'Moldova',
        'Pakistan',
        'Congo',
        'Honduras',
        'Sudan',
        'State of Palestine',
        'Ghana',
        'Marshall Islands',
        'Papua New Guinea',
        'Zambia',
        'Cambodia',
        'Mauritania',
        "Côte d'Ivoire",
        'Tuvalu',
        'Bangladesh',
        'Kyrgyzstan',
        'Cameroon',
        'Micronesia',
        'Senegal',
        'Sao Tome & Principe',
        'Kenya',
        'Vanuatu',
        'Tajikistan',
        'Tanzania',
        'Lesotho',
        'Comoros',
        'Nepal',
        'Yemen',
        'Zimbabwe',
        'Solomon Islands',
        'Benin',
        'Guinea',
        'Mali',
        'Kiribati',
        'Rwanda',
        'Afghanistan',
        'Chad',
        'Ethiopia',
        'Uganda',
        'Burkina Faso',
        'Haiti',
        'Guinea-Bissau',
        'Gambia',
        'Togo',
        'Madagascar',
        'Sierra Leone',
        'Liberia',
        'Mozambique',
        'Malawi',
        'Niger',
        'DR Congo',
        'Burundi',
        'Central African Republic',
        'American Samoa',
        'Cuba',
        'Northern Mariana Islands',
        'Andorra',
        'Guam',
    ],
};

export const PositiveCharacteristics: WordListWithoutId = {
    name: 'Positive Characteristics',
    symbol: <MdSentimentSatisfiedAlt size={14} />,
    words: [
        'Active',
        'Admirable',
        'Adventurous',
        'Agreeable',
        'Amiable',
        'Amusing',
        'Appreciative',
        'Athletic',
        'Authentic',
        'Benevolent',
        'Brave',
        'Bright',
        'Brilliant',
        'Calm',
        'Capable',
        'Caring',
        'Charming',
        'Cheerful',
        'Clean',
        'Clear-headed',
        'Clever',
        'Compassionate',
        'Confident',
        'Considerate',
        'Cooperative',
        'Courageous',
        'Courteous',
        'Creative',
        'Curious',
        'Dedicated',
        'Easygoing',
        'Educated',
        'Enthusiastic',
        'Ethical',
        'Exciting',
        'Extraordinary',
        'Fair',
        'Firm',
        'Focused',
        'Forgiving',
        'Friendly',
        'Generous',
        'Gentle',
        'Good-natured',
        'Grateful',
        'Happy',
        'Hardworking',
        'Helpful',
        'Heroic',
        'Honest',
        'Hopeful',
        'Humble',
        'Innocent',
        'Intelligent',
        'Inventive',
        'Joyful',
        'Kind',
        'Lively',
        'Loving',
        'Loyal',
        'Neat',
        'Nice',
        'Optimistic',
        'Organized',
        'Passionate',
        'Patient',
        'Peaceful',
        'Playful',
        'Polite',
        'Principled',
        'Reliable',
        'Respectful',
        'Responsible',
        'Self-disciplined',
        'Selfless',
        'Sincere',
        'Skillful',
        'Strong',
        'Sweet',
        'Thoughtful',
        'Trustworthy',
        'Understanding',
        'Unselfish',
        'Wise',
    ],
};

export const RichestCountries: WordListWithoutId = {
    name: 'Richest Countries',
    symbol: <MdAttachMoney size={14} />,
    words: [
        'Qatar',
        'Macao',
        'Luxembourg',
        'Singapore',
        'Brunei',
        'Ireland',
        'United Arab Emirates',
        'Kuwait',
        'Switzerland',
        'San Marino',
        'Norway',
        'Hong Kong',
        'United States',
        'Iceland',
        'Netherlands',
        'Denmark',
        'Saudi Arabia',
        'Austria',
        'Germany',
        'Sweden',
        'Australia',
        'Belgium',
        'Bahrain',
        'Canada',
        'Finland',
        'United Kingdom',
        'France',
        'Japan',
        'Oman',
        'Italy',
        'Malta',
        'New Zealand',
        'Aruba',
        'Spain',
        'Israel',
        'South Korea',
        'Czech Republic (Czechia)',
        'Slovenia',
        'Cyprus',
        'Estonia',
        'Lithuania',
        'Portugal',
        'Slovakia',
        'Trinidad and Tobago',
        'Bahamas',
        'Poland',
        'Malaysia',
        'Seychelles',
        'Hungary',
        'Saint Kitts & Nevis',
    ],
};

export const Science: WordListWithoutId = {
    name: 'Science',
    symbol: <MdScience size={14} />,
    words: ['science', 'technology', 'physics', 'chemistry', 'Einstein', 'NASA', 'experiment', 'astronomy'],
};

export const Strong: WordListWithoutId = {
    name: 'Strong',
    symbol: <CgGym size={14} />,
    words: [
        'power',
        'strong',
        'confident',
        'dominant',
        'potent',
        'command',
        'assert',
        'loud',
        'bold',
        'succeed',
        'triumph',
        'leader',
        'shout',
        'dynamic',
        'winner',
    ],
};

export const Weak: WordListWithoutId = {
    name: 'Weak',
    symbol: <BiSleepy size={14} />,
    words: [
        'weak',
        'surrender',
        'timid',
        'vulnerable',
        'weakness',
        'wispy',
        'withdraw',
        'yield',
        'failure',
        'shy',
        'follow',
        'lose',
        'fragile',
        'afraid',
        'loser',
    ],
};

export const Negation: WordListWithoutId = {
    name: 'Negation',
    symbol: <TbEqualNot size={14} />,
    words: ['not', 'without'],
};

const WORD_LISTS_WITHOUT_ID: WordListWithoutId[] = [
    Appearance,
    Arts,
    Career,
    Characteristics,
    Family,
    FemaleNouns,
    FemaleOccupations,
    FemalePersonNames,
    FemalePronouns,
    FemaleStereotypes,
    Intelligence,
    Insults,
    MaleNouns,
    MaleOccupations,
    MalePersonNames,
    MalePronouns,
    MaleStereotypes,
    NegativeCharacteristics,
    PositiveCharacteristics,
    Occupations,
    PoorestCountries,
    RichestCountries,
    Science,
    Strong,
    Weak,
    Negation,
];

export const WORD_LISTS: WordList[] = WORD_LISTS_WITHOUT_ID.map((wordList) => ({ ...wordList, id: v4() }));
